import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ContactContextService } from '@core/services/context/contact-context.service';
import { NavigationHelper } from '@core/services/core/navigation-helper.service';
import { OperationRegisterService } from '@core/services/core/operation-register.service';
import { UiHelperService } from '@core/services/core/ui-helper.service';
import { ForgotPasswordService } from '@core/services/http-services/forgot-password.service';
import { AnalyticsInterface } from '@models/analytics';
import { ForgetOperation } from '@models/operation-register';
import { ForgotPasswordOrigin } from '@models/user';
import { LoginRouting } from '@modules/login/models';
import { ForgetResultButton, ForgetResultOptions } from '@modules/login/pages/forget-result/forget-result.page';
import { RegisterRouting } from '@modules/register/models/register';
import { RegisterHelperService } from '@modules/register/services/register-helper.service';
import { TranslateService } from '@ngx-translate/core';
import { setSessionStorageData } from '@utils/session-storage';
import { obfuscateEmail, obfuscatePhoneCustomChar, padZeros } from '@utils/utils';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';

export interface ForgotPasswordState {
  documentNumber: string | undefined;
  phone: string | undefined;
  email: string | undefined;
  question: string | undefined;
  answer: string | undefined;
  newPassword: string | undefined;
  currentPassword: string | undefined;
  origin?: ForgotPasswordOrigin;
}

@Injectable({
  providedIn: 'root'
})
export class ForgotHelperService {

  private _options: Partial<ForgetResultOptions>;
  private _analytics: Partial<AnalyticsInterface>;
  private incorrectAttempts = new Map<string, number>();
  private notificationSendPinOK = new BehaviorSubject<boolean>(false);
  private textNotificationSendPinOK = new BehaviorSubject<string>('');

  constructor(
    private nav: NavigationHelper,
    private contactContext: ContactContextService,
    private ui: UiHelperService,
    private forgotPasswordService: ForgotPasswordService,
    private registerHelperService: RegisterHelperService,
    private or: OperationRegisterService,
    private translate: TranslateService,
  ) { }

  set options(opts: Partial<ForgetResultOptions>) {
    this._options = opts;
  }

  get options(): Partial<ForgetResultOptions> {
    return this._options;
  }

  set analytics(analytics: Partial<AnalyticsInterface>) {
    this._analytics = analytics;
  }

  get analytics(): Partial<AnalyticsInterface> {
    return this._analytics;
  }

  async validateUser(
    form: FormGroup,
    origin: ForgotPasswordOrigin,
    errorInfo: (error) => void) {

    if (form.invalid) {
      form.markAllAsTouched();
      return;
    }

    this.contactContext.setDocumentNumber(form.getRawValue().documentNumber);

    const documentNumber = padZeros(form.getRawValue().documentNumber.trim());
    const localState: ForgotPasswordState = this.getLocalState(form, documentNumber);
    await this.ui.presentLoading('forgot-1');
    this.forgotPasswordService
      .getUser(localState)
      .pipe(
        map(({ status, email }) => ({
          ...localState,
          email,
          status
        })),
        catchError(({ error }: HttpErrorResponse) => {
          return throwError({ error, origin });
        }),
        finalize(() => this.ui.dismissLoading('forgot-1'))
      )
      .subscribe(
        this.getSuccessCallbackForgotPasswordService(documentNumber, localState, form),
        this.getErrorCallbackForgotPasswordService(errorInfo, documentNumber)
      );
  }

  navToUserNotFound(): void {
    this.options = {
      data: {
        icon: 'assets/images/ko-cross.svg',
        title: 'LOGIN.FORGET.RESULT.USER_NOT_FOUND.TITLE',
        message: 'LOGIN.FORGET.RESULT.USER_NOT_FOUND.MESSAGE',
        backButton: true
      },
      buttons: {
        buttonsList: [
          {
            text: 'LOGIN.FORGET.RESULT.USER_NOT_FOUND.BUTTON_PRIMARY',
            expand: 'block',
            type: 'primary',
            onClick: () => this.navToRegister()
          },
          {
            text: 'LOGIN.FORGET.RESULT.USER_NOT_FOUND.BUTTON_LINK',
            type: 'link',
            onClick: () => this.nav.pop(),
          },
        ],
        reverseMobile: true
      }
    };
    this.navToResultForgot();
  }

  navToSendPinError(): void {
    this.options = {
      data: {
        icon: 'assets/images/ko-cross.svg',
        title: 'LOGIN.FORGET.RESULT.SEND_PIN_ERROR.TITLE',
        message: 'LOGIN.FORGET.RESULT.SEND_PIN_ERROR.MESSAGE',
        backButton: true
      },
      buttons: {
        buttonsList: [
          {
            text: 'LOGIN.FORGET.RESULT.SEND_PIN_ERROR.BUTTON_PRIMARY',
            expand: 'block',
            type: 'primary',
            onClick: () => this.navToRegister(),
          },
          {
            text: 'LOGIN.FORGET.RESULT.SEND_PIN_ERROR.BUTTON_LINK',
            type: 'link',
            onClick: () => this.nav.pop()
          }
        ],
        reverseMobile: true
      }
    };
    this.navToResultForgot();
  }

  navToUserSendTemporalKey(device: string, deviceText: string, sendTemporalPassword: () => Observable<any>): void {
    this.options = {
      data: {
        title: `LOGIN.FORGET.RESULT.USER_FOUND.${device}.TITLE`,
        message: `LOGIN.FORGET.RESULT.USER_FOUND.${device}.MESSAGE`,
        messageDynamic: deviceText,
        progressBar: '0.5',
        backButton: true
      },
      buttons: {
        buttonsList: this.getNavToUserSendTemporalKeyButtonList(
          device,
          sendTemporalPassword),
        reverseMobile: true
      }
    };
    this.navToResultForgot();
  }

  navToUserBlocked(state, documentNumber) {
    this.nav.toNew(['login', 'blocked'], {
      state: {
        documentNumber,
        email: state.email
      },
      queryParams: {
        oneDayBlocked: true
      }
    });
  }

  navToUserSessionLock(): void {
    this.options = {
      data: {
        icon: 'assets/images/ko-cross.svg',
        title: 'LOGIN.FORGET.RESULT.USER_SESSION_LOCK.TITLE',
        message: 'LOGIN.FORGET.RESULT.USER_SESSION_LOCK.MESSAGE',
        backButton: true
      },
      buttons: {
        buttonsList: [
          {
            text: 'LOGIN.FORGET.RESULT.USER_SESSION_LOCK.BUTTON_PRIMARY',
            expand: 'block',
            type: 'primary',
            onClick: () => this.nav.toNew(['support'])
          },
          {
            text: 'LOGIN.FORGET.RESULT.USER_SESSION_LOCK.BUTTON_LINK',
            type: 'link',
            onClick: () => this.navToRegister()
          }
        ],
        reverseMobile: true
      }
    };
    this.navToResultForgot();
  }

  navToResultOK(access: () => void) {
    this.options = {
      data: {
        icon: 'assets/images/ok-check.svg',
        title: 'LOGIN.FORGET.RESULT.OK.TITLE',
        message: 'LOGIN.FORGET.RESULT.OK.MESSAGE',
        backButton: false
      },
      buttons: {
        buttonsList: [
          {
            text: 'LOGIN.FORGET.RESULT.OK.BUTTON',
            expand: 'block',
            type: 'primary',
            onClick: () => access()
          }
        ],
        reverseMobile: true
      }
    };
    this.navToResultForgot();
  }

  navToResultKO() {
    this.options = {
      data: {
        icon: 'assets/images/ko-cross.svg',
        title: 'LOGIN.FORGET.RESULT.KO.TITLE',
        message: 'LOGIN.FORGET.RESULT.KO.MESSAGE',
        backButton: true
      },
      buttons: {
        buttonsList: [
          {
            text: 'LOGIN.FORGET.RESULT.KO.BUTTON_PRIMARY',
            expand: 'block',
            type: 'primary',
            onClick: () => this.nav.pop()
          },
          {
            text: 'LOGIN.FORGET.RESULT.KO.BUTTON_LINK',
            expand: 'block',
            type: 'link',
            onClick: () => this.nav.toNew(['support'])
          }
        ],
        reverseMobile: true
      }
    };
    this.navToResultForgot();
  }

  navToUserNotFoundByLogin() {
    this.options = {
      data: {
        icon: 'assets/images/ko-cross.svg',
        title: 'LOGIN.INACTIVE.RESULT.TITLE_KO',
        message: `LOGIN.INACTIVE.RESULT.DESCRIPTION_KO`,
        backButton: true
      },
      buttons: {
        middleButton: {
          text: 'LOGIN.INACTIVE.LINK',
          expand: 'block',
          type: 'link',
          onClick: () => this.nav.toNew(['support'])
        },
        buttonsList: [
          {
            text: 'LOGIN.INACTIVE.RESULT.BUTTON_BACK',
            expand: 'block',
            type: 'primary',
            onClick: () => this.nav.toNew(RegisterRouting.BASE_PATH + RegisterRouting.INITIAL_DATA)
          },
          {
            text: 'LOGIN.INACTIVE.RESULT.BUTTON_CONFIRM_KO',
            expand: 'block',
            type: 'link',
            onClick: () => this.nav.goLoginPage(true)
          }
        ],
        reverseMobile: true
      }
    };
    this.navToResultForgot();
  }

  navToUserLockUnauthorized() {
    this.options = {
      data: {
        icon: 'assets/images/ko-cross.svg',
        title: 'LOGIN.FORGET.RESULT.UNAUTHORIZED.TITLE',
        message: `LOGIN.FORGET.RESULT.UNAUTHORIZED.MESSAGE`,
        message2: `LOGIN.FORGET.RESULT.UNAUTHORIZED.MESSAGE2`,
        backButton: true
      },
      buttons: {
        buttonsList: [
          {
            text: 'LOGIN.FORGET.RESULT.UNAUTHORIZED.BUTTON_PRIMARY',
            expand: 'block',
            type: 'primary',
            onClick: () => this.nav.toNew(['support'])
          }
        ],
        reverseMobile: true
      }
    };
    this.navToResultForgot();
  }

  navToUserBlockedForSecurity(email: string, sendTemporalPassword?: () => Observable<any>) {
    this.options = {
      data: {
        icon: 'assets/images/ko-cross.svg',
        title: 'LOGIN.FORGET.RESULT.USER_BLOCKED_SECURITY.TITLE',
        message: this.translate.instant('LOGIN.FORGET.RESULT.USER_BLOCKED_SECURITY.MESSAGE', { email }),
        message2: `LOGIN.FORGET.RESULT.USER_BLOCKED_SECURITY.MESSAGE2`,
        backButton: true
      },
      buttons: {
        middleButton: this.getNavToUserBlockedForSecurityMiddleButton(sendTemporalPassword),
        buttonsList: this.getNavToUserBlockedForSecurityButtonList(),
        reverseMobile: true
      }
    };
    this.navToResultForgot();
  }

  navToLockUser(email: string) {
    this.options = {
      data: {
        icon: 'assets/images/ko-cross.svg',
        title: 'LOGIN.FORGET.RESULT.USER_LOCK.TITLE',
        message: `LOGIN.FORGET.RESULT.USER_LOCK.MESSAGE`,
        messageDynamic: email,
        message2: `LOGIN.FORGET.RESULT.USER_LOCK.MESSAGE2`,
        backButton: true
      },
      buttons: {
        buttonsList: [
          {
            text: 'LOGIN.FORGET.RESULT.USER_LOCK.BUTTON_PRIMARY',
            expand: 'block',
            type: 'primary',
            onClick: () => this.nav.toNew(['support'])
          }
        ],
        reverseMobile: true
      }
    };
    this.navToResultForgot();
  }

  navToRegister() {
    const url = RegisterRouting.BASE_PATH + RegisterRouting.INITIAL_DATA;
    const queryParams = this.nav.getQueryParams();
    this.nav.toNew(url, { queryParams });
  }

  navToResultForgot() {
    this.nav.toNew(['login', LoginRouting.FORGOT_PASSWORD, 'result']);
  }

  private getNavToUserBlockedForSecurityMiddleButton(
    sendTemporalPassword?: () => Observable<any>
  ): ForgetResultButton {
    let attemptsSendTemporalPass = 0;
    return {
      text: 'LOGIN.FORGET.RESULT.USER_FOUND_LOCKED.BUTTON',
      expand: 'block',
      type: 'link',
      onClick: () => {
        if (attemptsSendTemporalPass <= 2) {
          sendTemporalPassword().subscribe(
            (res) => {
              this.setHaveNotificationSendPinOK(true);
              setTimeout(() => {
                this.setHaveNotificationSendPinOK(false);
              }, 2000);
              attemptsSendTemporalPass++;
              if (attemptsSendTemporalPass > 2) {
                this.options.buttons.middleButton.text = '';
              }
            },
            (err) => {
              attemptsSendTemporalPass++;
              if (attemptsSendTemporalPass > 2) {
                this.options.buttons.middleButton.text = '';
              }
            }
          );
        }
      },
    };
  }

  private getNavToUserSendTemporalKeyButtonList(
    device: string,
    sendTemporalPassword: () => Observable<any>
  ) {
    let attemptsSendTemporalPass = 0;
    return [
      {
        text: 'LOGIN.FORGET.RESULT.RETURN_LOGIN',
        type: 'primary',
        onClick: () => { this.nav.newRoot('login'); }
      },
      {
        text: 'LOGIN.FORGET.RESULT.USER_FOUND.BUTTON',
        type: 'link',
        onClick: () => {
          if (attemptsSendTemporalPass <= 2) {
            sendTemporalPassword().subscribe(
              (res) => {
                this.setHaveNotificationSendPinOK(true);
                this.setTextNotificationSendPinOK(`FORWARDED_CODE_${device}`);
                setTimeout(() => {
                  this.setHaveNotificationSendPinOK(false);
                }, 2000);

                attemptsSendTemporalPass++;
                if (attemptsSendTemporalPass > 2) {
                  this.options.buttons.buttonsList[1].text = '';
                }
              },
              (err) => {
                attemptsSendTemporalPass++;
                if (attemptsSendTemporalPass > 2) {
                  this.options.buttons.buttonsList[1].text = '';
                }
              }
            );
          }
        },
      },
    ] as any[];
  }

  private getNavToUserBlockedForSecurityButtonList() {
    return [
      {
        text: 'LOGIN.FORGET.RESULT.USER_BLOCKED_SECURITY.BUTTON_PRIMARY',
        expand: 'block',
        type: 'primary',
        onClick: () => this.nav.toNew(['login'])
      },
      {
        text: 'LOGIN.FORGET.RESULT.USER_BLOCKED_SECURITY.BUTTON_LINK',
        expand: 'block',
        type: 'link',
        onClick: () => this.nav.toNew(['support'])
      },
    ] as any[];
  }

  private getNavToSendedTemporalKeyButtonList(sendTemporalPassword: () => Observable<any>) {
    let attemptsSendTemporalPass = 0;
    return [
      {
        text: 'LOGIN.FORGET.RESULT.RETURN_LOGIN',
        type: 'primary',
        onClick: () => { this.nav.newRoot('login'); }
      },
      {
        text: 'LOGIN.FORGET.RESULT.USER_FOUND_LOCKED.BUTTON',
        type: 'link',
        onClick: () => {
          if (attemptsSendTemporalPass <= 2) {
            sendTemporalPassword().subscribe(
              (res) => {
                this.setHaveNotificationSendPinOK(true);
                setTimeout(() => {
                  this.setHaveNotificationSendPinOK(false);
                }, 2000);
                attemptsSendTemporalPass++;
                if (attemptsSendTemporalPass > 2) {
                  this.options.buttons.buttonsList[1].text = '';
                }
              },
              (err) => {
                attemptsSendTemporalPass++;
                if (attemptsSendTemporalPass > 2) {
                  this.options.buttons.buttonsList[1].text = '';
                }
              }
            );
          }
        },
      },
    ] as any;
  }

  private getErrorCallbackForgotPasswordService(
    errorInfo: ({ error, origin }) => void,
    documentNumber: string) {
    return ({ error, origin }) => {
      const userNotFoundErrorKey = 'PASSWORD_RECOVERY.USER.NOT_FOUND';
      const { errorKey, errorCode } = error;
      const userNotFoundError = errorKey === userNotFoundErrorKey || errorCode === 'E#4101' || errorCode === 'E#4107';
      const otherError = errorCode === 'E#4102' || errorCode === 'E#4103';
      errorInfo(error);

      if (userNotFoundError) {
        this.forgetPassUserNotFoundErrorHandler(origin, documentNumber, errorKey, userNotFoundErrorKey);
        return;
      }

      if (otherError) {
        this.forgetPassErrorHandler(documentNumber, origin);
      }
    };
  }


  haveNotificationSendPinOK(): Observable<boolean> {
    return this.notificationSendPinOK.asObservable();
  }

  setHaveNotificationSendPinOK(value) {
    this.notificationSendPinOK.next(value);
  }

  haveTextNotificationSendPinOK(): Observable<string> {
    return this.textNotificationSendPinOK.asObservable();
  }

  setTextNotificationSendPinOK(value) {
    this.textNotificationSendPinOK.next(value);
  }


  private forgetPassErrorHandler(documentNumber: string, origin) {
    this.increaseIncorrectAttempts(documentNumber);
    const incorrectAttemptsAux = this.incorrectAttempts.get(
      documentNumber
    );
    if (incorrectAttemptsAux < 3) {
      this.navToUserNotFound();
    } else {
      setSessionStorageData('lockSessionForgot', true);
      const structureDevice = JSON.stringify(this.registerHelperService.getDeviceStructure(ForgetOperation.USER_NOT_FOUND));
      this.or.registerForgetPasswordOperation(
        ForgetOperation.ATTEMPTS_EXCEEDED_USER_LOGIN,
        origin || ForgotPasswordOrigin.FROM_LOGIN,
        documentNumber,
        structureDevice
      );
      this.navToUserSessionLock();
    }
  }

  private forgetPassUserNotFoundErrorHandler(
    origin,
    documentNumber: string,
    errorKey: string,
    userNotFoundErrorKey: string
  ) {
    if (errorKey === userNotFoundErrorKey) {
      const structureDevice = JSON.stringify(this.registerHelperService.getDeviceStructure(ForgetOperation.USER_NOT_FOUND));
      this.or.registerForgetPasswordOperation(
        ForgetOperation.USER_NOT_FOUND,
        origin || ForgotPasswordOrigin.FROM_LOGIN,
        documentNumber,
        structureDevice
      );
    }
    this.navToUserNotFound();
  }

  private increaseIncorrectAttempts(documentNumber: string) {
    const currentAttempts = this.incorrectAttempts.get(documentNumber);
    this.incorrectAttempts.set(documentNumber, currentAttempts + 1 || 1);
  }

  private getSuccessCallbackForgotPasswordService(
    documentNumber: string,
    localState: ForgotPasswordState,
    form: FormGroup
  ): (state) => void {
    return (state) => {
      const { status } = state;
      this.incorrectAttempts.set(documentNumber, 0);
      if (status === '6' || status === '9' || status === '5') {
        this.forgotPasswordService.sendTemporalPassword(localState)
          .subscribe(
            (res) => {
              this.navToUserSendTemporalKey(
                form.get('phone')?.value ? 'PHONE' : 'EMAIL',
                form.get('phone')?.value ?
                  obfuscatePhoneCustomChar(form.get('phone').value, '*') :
                  obfuscateEmail(form.get('email').value),
                () => this.forgotPasswordService.sendTemporalPassword(localState)
              );
            },
            (err) => {
              this.navToSendPinError();
            }
          );
        return;
      }
      if (status === '10') {
        this.navToUserBlocked(state, documentNumber);
      }
    };
  }

  private getLocalState(
    form: FormGroup,
    documentNumber: string): ForgotPasswordState {
    return {
      ...form.getRawValue(),
      origin,
      email: form.get('email')?.value,
      phone: form.get('phone')?.value,
      documentNumber
    };
  }
}
